import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
	useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import * as Yup from 'yup';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	useCloseCaseMutation,
	useOpenCaseMutation,
} from '@/api/member-management/admissionRequest';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useLocation, useParams } from 'react-router-dom';
import { useGetDicListQuery, useGetStaffAllListQuery } from '@/api/common';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';

interface Props {
	open: boolean;
	onClose: () => void;
}

const NewCloseCaseDialog = (props: Props) => {
	const theme = useTheme();
	console.log(theme);
	const { open, onClose } = props;
	const { t } = useTranslation();
	const [openAssignProgramWorkers, setOpenAssignProgramWorkers] =
		useState(false);
	const [closeform, setCloseForm] = useState({});
	const [closeCase] = useCloseCaseMutation();
	const { admissionRequestId } = useParams();
	const location = useLocation();
	const memberType = i18n.t(
		location.pathname.includes('admission-request-list')
			? 'member.admissionRequestLabel'
			: 'member.memberDetailsLabel'
	);

	const { data: closedReasonDicList } = useGetDicListQuery('closed_reason', {
		refetchOnMountOrArgChange: true,
	});

	const userInfo = useSelector(selectMyInfo);

	const { data: staffList }: any = useGetStaffAllListQuery(
		userInfo?.currentBranch?.id || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !userInfo?.currentBranch,
		}
	);

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		dateOfCaseClosed: Yup.string()
			.required(t('errorMessage.required'))
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		closedReason: Yup.string().required(t('errorMessage.required')),
		closedReasonSpecify: Yup.string().when('closedReason', {
			is: (a: string) => a && closedReasonIsOther(),
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
	});
	const formSchema2 = Yup.object()
		.shape({
			responsibleProgramWorkerId: Yup.string().nullable(),
			responsibleProfessionalStaffId: Yup.string().nullable(),
		})
		.test('oneRequired', t('member.closeCaseText3'), (values, testContext) => {
			const hasProgram = !!values?.responsibleProgramWorkerId;
			const hasProfessional = !!values?.responsibleProfessionalStaffId;

			if (!hasProgram && !hasProfessional) {
				return testContext.createError({
					path: 'responsibleProgramWorkerId',
					message: t('member.closeCaseText3'),
				});
			}
			return true;
		});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});
	const watchClosedReason = methods.watch('closedReason');
	const methods2 = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema2),
	});

	const handleClose = () => {
		methods.reset({});
		onClose();
	};

	const handleCloseAssignCaseWorkers = () => {
		methods2.reset({});
		onClose();
	};

	const onNext: SubmitHandler<any> = async (data) => {
		setOpenAssignProgramWorkers(true);
		setCloseForm(data);
	};

	const onSubmit: SubmitHandler<any> = async (data) => {
		const response = await closeCase({
			...data,
			...closeform,
			id: admissionRequestId,
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateMemberSuccess', { type: memberType }), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
			handleCloseAssignCaseWorkers();
		}
	};

	const closedReasonIsOther = () => {
		const obj = closedReasonDicList?.data.find(
			(item: any) => item.id == watchClosedReason
		);
		if (obj && obj.code == '7e') return true;
		return false;
	};

	return (
		<>
			<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
				<DialogTitle variant='h5'>{t('button.closeCase')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('member.closeCaseText')}
					</Typography>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onNext)}>
							<Grid
								container
								spacing={2}
								marginTop={0}
								marginBottom={2}
								alignItems='center'
							>
								<Grid size={12}>
									<AppDatePicker
										maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
										control={methods.control}
										name='dateOfCaseClosed'
										label={t('member.dateCaseClosed') + '*'}
									/>
								</Grid>
								<Grid size={12}>
									<AppDropDownMenu
										control={methods.control}
										name='closedReason'
										label={t('member.closedReason') + '*'}
										options={(closedReasonDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
										handleChange={() => {
											methods.setValue('closedReasonSpecify', '');
											methods.clearErrors('closedReasonSpecify');
										}}
									/>
								</Grid>
								<Grid size={12}>
									<AppTextField
										disabled={!closedReasonIsOther()}
										control={methods.control}
										name='closedReasonSpecify'
										label={`${t('member.closedReasonSpecify')}${
											closedReasonIsOther() ? '*' : ''
										}`}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onNext)}
					>
						{t('button.next')}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={openAssignProgramWorkers}
				onClose={handleCloseAssignCaseWorkers}
				maxWidth='sm'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{t('member.assignProgramWorkers')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleCloseAssignCaseWorkers}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('member.closeCaseText2')}
					</Typography>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('member.closeCaseText3')}
					</Typography>
					<FormProvider {...methods2}>
						<form onSubmit={methods2.handleSubmit(onSubmit)}>
							<Grid
								container
								spacing={2}
								marginTop={0}
								marginBottom={2}
								alignItems='center'
							>
								<Grid size={12}>
									<AppDropDownMenu
										optional={true}
										control={methods2.control}
										name='responsibleProgramWorkerId'
										label={t('member.responsibleProgramWorker')}
										options={staffList?.data || []}
									/>
								</Grid>
								<Grid size={12}>
									<AppDropDownMenu
										optional={true}
										control={methods2.control}
										name='responsibleProfessionalStaffId'
										label={t('member.responsibleProfessionalStaff')}
										options={staffList?.data || []}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button
						sx={{ textTransform: 'none' }}
						onClick={() => handleCloseAssignCaseWorkers()}
					>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods2.handleSubmit(onSubmit)}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default NewCloseCaseDialog;
